import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  GridView as GridViewIcon,
  BuildCircle as BuildCircleIcon,
  Rocket as RocketIcon,
  Engineering as EngineeringIcon,
  Description as DocumentIcon,
  UploadFile as UploadFileIcon,
  AddTask as AddTaskIcon,
  Update as UpdateIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  AccountCircle as AccountCircleIcon,
  TaskAlt as UATIcon,
  AccountTree as FlowIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { PageContainer, Card, Section, PageTitleContainer, HtmlRenderer } from '../components/common';
import PermissionGate from '../components/common/PermissionGate';
import { AuthContext } from '../contexts/AuthContext';
import NotFoundPage from './NotFound';
import ImageAvatarSelector from '../components/layout/ImageAvatarSelector';
import CreateProject from './CreateProject';
import useProject from '../hooks/useProject';
import useNotification from '../hooks/useNotification';
import documentService from '../services/documentService';
import featureService from '../services/featureService';

/**
 * ProjectDetail Component
 * Displays detailed information about a project with actions and statistics
 * 
 * @returns {React.ReactElement} ProjectDetail component
 */
const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [setProjectName, setDocumentName] = useOutletContext();
  const { showNotification } = useNotification();
  const { hasPermission } = useContext(AuthContext);
  const [accessDenied, setAccessDenied] = useState(false);

  // Check if the URL has the edit=true query parameter
  const urlParams = new URLSearchParams(location.search);
  const shouldOpenEditDialog = urlParams.get('edit') === 'true';
  const {
    project,
    updateProject,
    isLoadingProject,
    projectError,
    refetchProject
  } = useProject({ projectId });

  // Local state for documents and user stories
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  const [userStories, setUserStories] = useState([]);
  const [userStoriesLoading, setUserStoriesLoading] = useState(false);
  const [userStoriesError, setUserStoriesError] = useState(null);

  // Statistics for documents and user stories
  const [documentStats, setDocumentStats] = useState({
    newThisWeek: 0,
    draft: 0,
    published: 0,
    totalCount: 0,
    recentDocuments: []
  });

  const [userStoryStats, setUserStoryStats] = useState({
    completed: 0,
    needsFeedback: 0,
    totalCount: 0,
    recentStories: []
  });

  // Edit project dialog state
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editProjectData, setEditProjectData] = useState({
    name: '',
    description: '',
    status: 'active',
    prdCreationMode: 'standard'
  });
  const [editProjectErrors, setEditProjectErrors] = useState({});
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);

  // Avatar state
  const [predefinedAvatarDialogOpen, setPredefinedAvatarDialogOpen] = useState(false);
  const [avatarName, setAvatarName] = useState('');
  const [projectAvatar, setProjectAvatar] = useState({
    name: '',
    type: '',
    color: '',
    options: {},
    photoUrl: null,
    backgroundColor: '#e3f2fd'
  });


  // Set project name for breadcrumbs when project data is loaded
  useEffect(() => {
    if (project?.name) {
      setProjectName(project.name);

      // Set avatar if it exists
      if (project.avatar) {
        setProjectAvatar(project.avatar);
      }

      // Initialize edit project form data
      setEditProjectData({
        name: project.name || '',
        description: project.description || '',
        status: project.status || 'active',
        prdCreationMode: project.prdCreationMode || 'standard'
      });

      // If shouldOpenEditDialog is true and project data is loaded, open the edit dialog
      if (shouldOpenEditDialog) {
        setEditProjectDialogOpen(true);

        // Clear the query parameter from the URL without navigation
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }

    return () => {
      setProjectName('');
    };
  }, [project, setProjectName, shouldOpenEditDialog]);

  // Load documents when project is loaded
  useEffect(() => {
    if (!project) return;

    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const documentData = await documentService.getProjectDocuments(projectId);
        setDocuments(documentData);

        // Calculate document statistics
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const newThisWeek = documentData.filter(doc =>
          new Date(doc.createdAt) > oneWeekAgo
        ).length;

        const draftDocs = documentData.filter(doc =>
          doc.status.toLowerCase() === 'draft'
        ).length;

        const publishedDocs = documentData.filter(doc =>
          doc.status.toLowerCase() === 'published'
        ).length;

        // Get recent documents (newest first)
        const recentDocs = [...documentData]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 3);

        setDocumentStats({
          newThisWeek,
          draft: draftDocs,
          published: publishedDocs,
          totalCount: documentData.length,
          recentDocuments: recentDocs
        });
      } catch (err) {
        console.error('Error loading documents:', err);
        setDocumentsError(err.message || 'Error loading documents');
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, [projectId, project]);

  // Load user stories when project is loaded
  useEffect(() => {
    if (!project) return;

    const loadUserStories = async () => {
      try {
        setUserStoriesLoading(true);
        const userStoryData = await featureService.getProjectUserStories(projectId);
        setUserStories(userStoryData);

        if (userStoryData) {
          // Calculate user story statistics
          const completedStories = userStoryData.filter(story =>
            story.status === 'completed'
          ).length;

          const needsFeedbackStories = userStoryData.filter(story =>
            story.status === 'testing'
          ).length;

          // Get recent stories (newest first)
          const recentStories = [...userStoryData]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 3);

          setUserStoryStats({
            completed: completedStories,
            needsFeedback: needsFeedbackStories,
            totalCount: userStoryData.length,
            recentStories
          });
        }
      } catch (err) {
        console.error('Error loading user stories:', err);
        setUserStoriesError(err.message || 'Error loading user stories');
      } finally {
        setUserStoriesLoading(false);
      }
    };

    loadUserStories();
  }, [projectId, project]);

  // Project edit dialog handlers
  const handleOpenEditProjectDialog = () => {
    // Check if project is available before opening the dialog
    if (!project || (!project._id && !project.id)) {
      console.error("Cannot edit project - project data not fully loaded");
      showNotification({
        message: 'Cannot edit project. Please try again after the project data has loaded.',
        type: 'error'
      });
      return;
    }

    setEditProjectDialogOpen(true);
  };

  const handleCloseEditProjectDialog = () => {
    setEditProjectDialogOpen(false);
    // Refetch project data to ensure we have the latest updates
    if (refetchProject) {
      refetchProject();
    }
  };

  const handleEditProjectInputChange = (e) => {
    const { name, value } = e.target;
    setEditProjectData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field if it exists
    if (editProjectErrors[name]) {
      setEditProjectErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateProjectForm = () => {
    const errors = {};

    if (!editProjectData.name.trim()) {
      errors.name = 'Project name is required';
    } else if (editProjectData.name.length > 100) {
      errors.name = 'Project name cannot exceed 100 characters';
    }

    setEditProjectErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProject = async () => {
    if (!validateProjectForm()) {
      return;
    }

    try {
      setIsSubmittingProject(true);

      // Preserve fields we don't edit in the form
      const updatedProjectData = {
        ...project,
        name: editProjectData.name,
        description: editProjectData.description,
        status: editProjectData.status,
        prdCreationMode: editProjectData.prdCreationMode
      };

      await updateProject(projectId, updatedProjectData);

      // Close the dialog after successful update
      handleCloseEditProjectDialog();

      // Set the new project name in the breadcrumbs
      setProjectName(editProjectData.name);

    } catch (error) {
      console.error('Error updating project:', error);
      // Handle the error (could set a form-level error message)
    } finally {
      setIsSubmittingProject(false);
    }
  };

  // Avatar dialog handlers
  const handleOpenAvatarDialog = () => {
    setPredefinedAvatarDialogOpen(true);
  };

  const handleClosePredefinedAvatarDialog = () => {
    setPredefinedAvatarDialogOpen(false);
  };

  const handleAvatarNameChange = (e) => {
    setAvatarName(e.target.value);
  };

  const handleSelectPredefinedAvatar = async (avatar) => {
    try {
      // Create new avatar with predefined settings
      const newAvatar = {
        name: avatarName || avatar.name,
        backgroundColor: avatar.backgroundColor,
        photoUrl: avatar.photoUrl,
        type: 'robot',
        color: avatar.color
      };

      await updateProject(projectId, {
        ...project,
        avatar: newAvatar
      });

      // Update local state
      setProjectAvatar(newAvatar);

      // Close dialog
      handleClosePredefinedAvatarDialog();
    } catch (err) {
      console.error('Error setting avatar:', err);
    }
  };


  // Configuration for development phase tiles
  const foundationTiles = [
    {
      id: 'foundation',
      title: 'Foundation',
      icon: <GridViewIcon style={{ display: 'block', marginBottom: '10px' }} />,
      color: '#e8f5e9',
      iconColor: '#4caf50',
      link: `/projects/${projectId}/prd-workflow#foundation`,
      permission: 'project.foundation'
    },
    {
      id: 'build',
      title: 'Build',
      icon: <BuildCircleIcon style={{ display: 'block', marginBottom: '10px' }} />,
      color: '#e3f2fd',
      iconColor: '#2196f3',
      link: `/projects/${projectId}/prd-workflow/edit?step=2&type=build`,
      permission: 'project.build'
    },
    {
      id: 'uat',
      title: 'UAT',
      icon: <UATIcon style={{ display: 'block', marginBottom: '10px', fontSize: '48px' }} />,
      color: '#f1f8e9',
      iconColor: '#689f38',
      link: `/projects/${projectId}/prd-workflow/edit?step=10&type=uat`,
      permission: 'project.uat'
    },
    {
      id: 'deploy',
      title: 'Deploy',
      icon: <RocketIcon style={{ display: 'block', marginBottom: '10px' }} />,
      color: '#fff3e0',
      iconColor: '#ff9800',
      link: `/projects/${projectId}/prd-workflow/edit?step=8&type=user-stories`,
      permission: 'project.deploy'
    },
    {
      id: 'maintenance',
      title: 'Maintenance',
      icon: <EngineeringIcon style={{ display: 'block', marginBottom: '10px' }} />,
      color: '#f3e5f5',
      iconColor: '#9c27b0',
      link: `/projects/${projectId}/prd-workflow/edit?step=9&type=final-prd`,
      permission: 'project.maintenance'
    }
  ];

  // Configuration for document tiles
  const documentTiles = [
    {
      id: 'uploaded-documents',
      title: 'Project uploaded documents',
      icon: <UploadFileIcon fontSize="large" />,
      color: '#fff3e0',
      iconColor: '#ff9800',
      link: `/projects/${projectId}/uploaded-documents`,
      permission: 'project.documents.uploaded'
    },
    {
      id: 'prd',
      title: 'Project generated documents',
      icon: <DocumentIcon fontSize="large" />,
      color: '#e8f5e9',
      iconColor: '#4caf50',
      link: `/projects/${projectId}/documents`,
      permission: 'project.documents.generated'
    },
    // {
    //   id: 'flow-designer',
    //   title: 'Flow designer',
    //   icon: <FlowIcon fontSize="large" />,
    //   color: '#e8f5e9',
    //   iconColor: '#4caf50',
    //   link: `/projects/${projectId}/flow-designer`
    // },
    {
      id: 'new-feature',
      title: 'New feature',
      icon: <AddTaskIcon fontSize="large" />,
      color: '#e3f2fd',
      iconColor: '#2196f3',
      link: `/projects?type=new-feature&parentId=${projectId}`,
      permission: 'project.feature.new'
    },
    {
      id: 'change-request',
      title: 'Change request',
      icon: <UpdateIcon fontSize="large" />,
      color: '#fff3e0',
      iconColor: '#ff9800',
      link: `/projects?type=change-request&parentId=${projectId}`,
      permission: 'project.change.request'
    }
  ];

  // Render loading state
  if (isLoadingProject) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render error/not found state
  if (projectError || !project) {
    return <NotFoundPage />;
  }

  // This block is kept but unreachable for backward compatibility
  if (false && !project) {
    return (
      <Section
        title="Project not found"
        sx={{ mt: 4, maxWidth: 600, mx: 'auto' }}
      >
        <Alert severity="info" sx={{ mb: 3 }}>
          Project not found. The project ID may be incorrect or the project may have been deleted.
          <br /><br />
          Project ID: <strong>{projectId}</strong>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 4 }}>
          <Typography variant="h6" gutterBottom>Troubleshooting:</Typography>
          <Typography variant="body2" paragraph>
            1. Check if you're logged in with the correct account that has access to this project.
          </Typography>
          <Typography variant="body2" paragraph>
            2. Verify that the project ID in the URL is correct.
          </Typography>
          <Typography variant="body2" paragraph>
            3. Click "Debug Projects" to see a list of available projects.
          </Typography>
          <Typography variant="body2" paragraph>
            4. Make sure the backend server is running correctly.
          </Typography>
        </Box>
      </Section>
    );
  }

  // Render project detail
  return (
    <PageContainer fullWidth className="project-detail-container">
      {/* Project Header */}
      <PageTitleContainer
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" component="span" fontWeight={600}>
                {project?.name || "Project"}
              </Typography>
            </Box>
          </Box>
        }
        rightContent={
          <PermissionGate permission="project.edit">
            <Tooltip title="Edit project">
              <IconButton
                color="primary"
                onClick={handleOpenEditProjectDialog}
                sx={{ ml: 2 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </PermissionGate>
        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 4,
          mt: 0
        }}
      >
        {project?.description && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', display: 'block' }}>
            <Box sx={{ display: 'block', maxHeight: '200px', overflow: 'auto' }}>
              <HtmlRenderer content={project.description} />
            </Box>
          </Box>
        )}
      </PageTitleContainer>

      {/* Project Building Blocks */}
      <Section
        title="Project building blocks"
        sx={{
          mb: 4,
          mt: 4,
          px: 3,
          pt: '24px',
          pb: '24px',
          '& h2': {
            marginBottom: 3,
            pl: 2 // Align with grid content
          },
          '& > div': { marginBottom: 0 }
        }}
        variant="elevation"
        elevation={0}
      >
        {/* Access Denied Alert */}
        {accessDenied && (
          <Alert severity="error" sx={{ mb: 2, mx: { xs: 0, sm: '2%', md: 4 }, '& .MuiAlert-icon': { pl: 3 }, '& .MuiAlert-action': { pr: 3 },  px: '20px !important' }} onClose={() => setAccessDenied(false)}>
            Access denied. You don't have permission to access this section.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: { xs: 0, sm: '2%', md: 4 } }}>
          {foundationTiles
            .filter(tile => {
              // Hide maintenance tile if project type is not 'project'
              if (tile.id === 'maintenance' && project?.type !== 'project') {
                return false;
              }
              // Hide tiles if user doesn't have permission
              if (tile.permission && !hasPermission(tile.permission)) {
                return false;
              }
              return true;
            })
            .map(tile => (
            <Box
              key={tile.id}
              sx={{
                width: { xs: '100%', sm: '45%', md: '16%' },
                mr: { xs: 0, sm: '2.5%', md: 4 },
                mb: 2
              }}
            >
              <Box
                onClick={() => {
                  if (tile.permission && !hasPermission(tile.permission)) {
                    setAccessDenied(true);
                    setTimeout(() => setAccessDenied(false), 3000);
                    return;
                  }
                  window.open(tile.link, '_blank');
                }}
                sx={{
                  height: 140,
                  width: '100%',
                  bgcolor: tile.color,
                  borderRadius: 1,
                  boxShadow: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  p: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Box
                  sx={{
                    color: tile.iconColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  {tile.id === 'foundation' && <GridViewIcon sx={{ fontSize: 45 }} />}
                  {tile.id === 'build' && <BuildCircleIcon sx={{ fontSize: 45 }} />}
                  {tile.id === 'uat' && <UATIcon sx={{ fontSize: 45 }} />}
                  {tile.id === 'deploy' && <RocketIcon sx={{ fontSize: 45 }} />}
                  {tile.id === 'maintenance' && <EngineeringIcon sx={{ fontSize: 45 }} />}
                </Box>

                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 1,
                    fontWeight: 500,
                    display: 'block',
                    color: 'text.primary',
                    width: '100%'
                  }}
                >
                  {tile.title}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>


      {/* Project Details - hidden if no visible tiles */}
      {(() => {
        const usersTile = {
          id: 'users',
          title: 'Project users',
          icon: <AccountCircleIcon fontSize="large" />,
          color: '#ecf0fa',
          iconColor: '#6772e5',
          link: `/projects/${projectId}/users`,
          permission: 'project.members.manage'
        };
        const integrationsTile = {
          id: 'integrations',
          title: 'Integrations',
          icon: <FlowIcon fontSize="large" />,
          color: '#f0e6fa',
          iconColor: '#a259ff',
          link: `/projects/${projectId}/integrations`,
          permission: 'project.dashboard.settings'
        };
        const allDetailTiles = [...documentTiles, usersTile, integrationsTile];
        const visibleDetailTiles = allDetailTiles.filter(tile => {
          const tilesToHide = ['flow-designer', 'new-feature', 'change-request', 'users'];
          if (tilesToHide.includes(tile.id) && project?.type !== 'project') return false;
          if (tile.permission && !hasPermission(tile.permission)) return false;
          return true;
        });
        if (visibleDetailTiles.length === 0) return null;
        return (
      <Section
        title="Project Details"
        sx={{
          mb: 4,
          mt: 4,
          px: 3,
          pt: '24px',
          pb: '24px',
          '& h2': {
            marginBottom: 3,
            pl: 2
          },
          '& > div': { marginBottom: 0 }
        }}
        variant="elevation"
        elevation={0}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: 6 }}>
          {visibleDetailTiles.map(tile => (
            <Box
              key={tile.id}
              sx={{
                width: { xs: '40%', sm: '18%', md: '14%', lg: '11%' },
                mr: 6,
                mb: 2
              }}
            >
              <Box
                onClick={() => {
                  if (tile.id === 'users' || tile.id === 'project-members') {
                    navigate(tile.link);
                  } else {
                    window.open(tile.link, '_blank');
                  }
                }}
                sx={{
                  height: 110,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                    transform: 'translateY(-2px)',
                    boxShadow: 0
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    p: 1,
                    textAlign: 'center'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 1.5
                    }}
                  >
                    {React.cloneElement(tile.icon, {
                      style: {
                        fontSize: '1.4rem',
                        color: '#555555'
                      }
                    })}
                  </Box>

                  <Typography
                    variant="body1"
                    component="h3"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#333333',
                      lineHeight: 1.2,
                      textAlign: 'center'
                    }}
                  >
                    {tile.title}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>
        );
      })()}

      {/* Project Statistics - Hidden as requested */}
      {/* <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          Document Statistics and User Story Statistics sections hidden as requested
        </Grid>
      </Box> */}

      {/* Edit Project Dialog */}
      <Dialog
        open={editProjectDialogOpen}
        onClose={handleCloseEditProjectDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            boxShadow: '0 0 10px rgba(0,0,0,0.05)',
            maxWidth: '90vw',
            width: '90vw',
            maxHeight: '90vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            m: 0,
            p: 0
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
            pb: '14px',
            pt: '14px',
            pl: 5,
            pr: '10px',
            bgcolor: 'white'
          }}
        >
          <Typography variant="h6" fontWeight={600}>Edit project</Typography>
          <IconButton onClick={handleCloseEditProjectDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, m: 0, bgcolor: 'white', overflow: 'hidden' }}>
          {/* Import the CreateProject component with editMode=true */}
          <Box sx={{ p: 0, m: 0, bgcolor: 'white' }}>
            {editProjectDialogOpen && (
              <CreateProject
                projectId={project?._id || project?.id}
                editMode={true}
                onClose={handleCloseEditProjectDialog}
                showHeader={false} // Don't show the component's own header
                sx={{p:0, m:0}}
                project={project} // Pass the full project object as well
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Predefined Avatars Dialog */}
      <Dialog
        open={predefinedAvatarDialogOpen}
        onClose={handleClosePredefinedAvatarDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h6">Select project avatar</Typography>
          <IconButton onClick={handleClosePredefinedAvatarDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose a predefined avatar for your project. These avatars will be consistent across all environments.
            </Typography>

            <TextField
              label="Avatar name"
              variant="outlined"
              fullWidth
              value={avatarName}
              onChange={handleAvatarNameChange}
              sx={{ mb: 3 }}
              placeholder="Enter a name for your project avatar"
              helperText="You can customize the name of the selected avatar"
            />
          </Box>

          <ImageAvatarSelector
            onSelectAvatar={(avatar) => {
              // Pre-populate the avatar name field when an avatar is selected
              if (!avatarName) {
                setAvatarName(avatar.name);
              }

              // Create a copy of the avatar with the user's custom name
              const avatarWithCustomName = {
                ...avatar,
                name: avatarName || avatar.name
              };

              handleSelectPredefinedAvatar(avatarWithCustomName);
            }}
          />
        </DialogContent>
      </Dialog>

    </PageContainer>
  );
};

export default ProjectDetail;